/**
 * Portfolio Detail Page
 * Single portfolio item view
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiExternalLink, FiGithub } from 'react-icons/fi';
import api from '../services/api';
import analytics from '../utils/analytics';

const PortfolioDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await api.get(`/content-items/${id}`);
        const projectData = response.data.data;
        setProject(projectData);
        
        // Track portfolio item view
        if (projectData) {
          analytics.viewPortfolioItem(projectData._id, projectData.title);
        }
      } catch (error) {
        // Error is handled by the UI (loading state and error display)
        // In production, this could be logged to an error tracking service
      } finally {
        setLoading(false);
      }
    };
    
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingText>Loading project...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!project) {
    return (
      <ErrorContainer>
        <ErrorText>Project not found</ErrorText>
        <BackLink to="/projects">
          <FiArrowLeft /> Back to Projects
        </BackLink>
      </ErrorContainer>
    );
  }

  return (
    <DetailContainer>
      <Container>
        <BackLink to="/projects">
          <FiArrowLeft /> Back to Projects
        </BackLink>

        <Header>
          <TitleSection>
            <CategoryBadge>{project.category}</CategoryBadge>
            <Title>{project.title}</Title>
            <Description dangerouslySetInnerHTML={{ __html: project.description }} />
          </TitleSection>

          {(project.links?.live || project.links?.github) && (
            <LinksSection>
              {project.links.live && (
                <LinkButton href={project.links.live} target="_blank" rel="noopener noreferrer">
                  <FiExternalLink /> Live Demo
                </LinkButton>
              )}
              {project.links.github && (
                <LinkButton href={project.links.github} target="_blank" rel="noopener noreferrer">
                  <FiGithub /> View Code
                </LinkButton>
              )}
            </LinksSection>
          )}
        </Header>

        {project.images && project.images.length > 0 && (
          <ImageGallery>
            {project.images.map((image, index) => (
              <GalleryImage key={index} src={image.url} alt={image.alt || project.title} />
            ))}
          </ImageGallery>
        )}

        <ContentGrid>
          {project.technologies && project.technologies.length > 0 && (
            <Section>
              <SectionTitle>Technologies Used</SectionTitle>
              <TechList>
                {project.technologies.map((tech, index) => (
                  <TechItem key={index}>{tech}</TechItem>
                ))}
              </TechList>
            </Section>
          )}

          {project.tags && project.tags.length > 0 && (
            <Section>
              <SectionTitle>Tags</SectionTitle>
              <TagList>
                {project.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </TagList>
            </Section>
          )}
        </ContentGrid>
      </Container>
    </DetailContainer>
  );
};

const DetailContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 0 3rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  margin-bottom: 2rem;
  transition: color 0.3s ease;
  cursor: pointer;
  position: relative;
  z-index: 10;

  &:hover {
    color: var(--accent-primary);
  }
`;

const Header = styled.div`
  margin-bottom: 3rem;
`;

const TitleSection = styled.div`
  margin-bottom: 2rem;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
  line-height: 1.8;
  max-width: 800px;
`;

const LinksSection = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const ImageGallery = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const GalleryImage = styled.img`
  width: 100%;
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div``;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const TechList = styled.ul`
  list-style: none;
  display: grid;
  gap: 0.75rem;
`;

const TechItem = styled.li`
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  border-left: 3px solid var(--accent-primary);
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Tag = styled.span`
  padding: 0.5rem 1rem;
  background: var(--bg-secondary);
  border-radius: 2rem;
  color: var(--text-secondary);
`;

const LoadingContainer = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingText = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
`;

const ErrorContainer = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
`;

const ErrorText = styled.p`
  font-size: 1.5rem;
  color: var(--text-secondary);
`;

export default PortfolioDetail;
